import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Lock, Sparkle} from '../../../icons';
import {Spinner} from '../../../Spinner';
import {LoadErrorState} from '../../../elements/LoadErrorState';
import {adLevelLabel, adgroupLevelLabel} from '../../../../utils/channelLevelLabels';
import {PlatformLogo} from '../PlatformLogo';
import {platformName} from '../platformsText';
import {AttributionRuleFields} from '../../../ga4_attribution/AttributionRuleFields';
import {Ga4AttributionModal} from '../../../ga4_attribution/Ga4AttributionModal';
import {
    isAutomatic, linkedRules, rowIsEditable, suggestionOffered, suggestionTextKey,
} from '../../../ga4_attribution/attributionModel';
import {RAW, percentText, ruleSummaryText} from '../../../ga4_attribution/attributionText';
import {useGa4AttributionRules} from './useGa4AttributionRules';
import '../../../../sass/elements/_ga4-attribution.scss';

const NS = 'ga4_attribution.agency';
export const ANCHOR = 'atribucion-ga4';

/**
 * «Cómo GA4 identifica tu pauta» (UD-10): la regla de cruce de la agencia, DENTRO de la conexión de GA4
 * en Conexiones › Plataformas de la 2.1, debajo de su fila (`PlatformsV2` lo monta ahí). Con qué
 * parámetro de la URL identifica la agencia el conjunto o grupo de anuncios y el anuncio en cada
 * plataforma, para que Dashbo sepa de cuál viene cada sesión de GA4.
 *
 * Sólo muestra lo que aplica (diseño por excepción): sin GA4 conectada (`has_ga4_connection`) no existe,
 * y lista sólo las plataformas con alguna cuenta vinculada a una propiedad (`ga4_linked_accounts`).
 * Google es una línea fija (se cruza solo, por la vinculación con Google Ads). Las demás, UNA línea de
 * resumen con «Cambiar», que abre los selects ahí mismo; «Guardar» aparece sólo con cambios y guarda esa
 * plataforma. Con la regla en «No cruzar» y una sugerencia del backend, la sugerencia es la acción
 * principal: «Usar esta regla» la guarda de un clic.
 *
 * Tolera el backend viejo: con 404 o sin `has_ga4_connection` no se dibuja, y mientras carga tampoco (sin
 * un esqueleto que aparece y desaparece). Sin permiso, se lee igual, sin acciones y con el motivo a la
 * vista; el 403 al guardar la deja en ese mismo estado.
 *
 * `#atribucion-ga4` en la URL (el «Ver la regla» de la cuenta, el «Cambiar» de una línea, la nota del
 * clásico) lo trae a la vista cuando termina de cargar.
 */
export function Ga4AttributionRulesCard() {
    const {t, i18n} = useTranslation();
    const data = useGa4AttributionRules();
    const sectionRef = useRef(null);
    const scrolled = useRef(false);
    const [openChannels, setOpenChannels] = useState([]);
    // A dónde va el foco después de abrir o cerrar una línea: lo que lo tenía se desmonta en los dos casos.
    const [focusTarget, setFocusTarget] = useState(null);
    const changeButtons = useRef({});
    const editors = useRef({});
    const [account, setAccount] = useState(null);
    // Reactstrap sigue dibujando el contenido mientras el modal se va: se dibuja el último.
    const lastAccount = useRef(null);
    if (account) lastAccount.current = account;
    const shownAccount = account || lastAccount.current;

    const lines = linkedRules(data.rules);
    const visible = data.status === 'ready' && data.hasGa4Connection && lines.length > 0;
    useEffect(() => {
        if (!visible || scrolled.current) return;
        if (typeof window === 'undefined' || window.location.hash !== `#${ANCHOR}`) return;
        scrolled.current = true;
        if (sectionRef.current && sectionRef.current.scrollIntoView) {
            sectionRef.current.scrollIntoView({block: 'start'});
        }
    }, [visible]);

    useEffect(() => {
        if (!focusTarget) return;
        const element = focusTarget.kind === 'edit'
            ? editors.current[focusTarget.channel] && editors.current[focusTarget.channel].querySelector('select')
            : changeButtons.current[focusTarget.channel];
        if (element && element.focus) element.focus();
        setFocusTarget(null);
    }, [focusTarget]);

    if (data.status === 'loading' || data.status === 'absent') return null;
    if (data.status === 'ready' && !visible) return null;

    const frame = (content) => (
        <section className="apl-ga4-attr" id={ANCHOR} ref={sectionRef} aria-labelledby="apl-ga4-attr-title"
                 data-testid="ga4-attr-block">
            <h4 className="apl-ga4-attr__title" id="apl-ga4-attr-title">{t(`${NS}.title`)}</h4>
            {content}
        </section>
    );

    if (data.status === 'error') {
        return frame(
            <LoadErrorState title={t(`${NS}.load_error`)} text={t(`${NS}.load_error_explanation`)}
                            retryLabel={t(`${NS}.load_error_retry`)} onRetry={data.retry}/>,
        );
    }

    const language = i18n.language;
    const readOnly = !data.canEdit;

    const open = (channel) => {
        data.resetDraft(channel);
        setOpenChannels((current) => (current.includes(channel) ? current : [...current, channel]));
        setFocusTarget({channel, kind: 'edit'});
    };
    const close = (channel) => {
        setOpenChannels((current) => current.filter((each) => each !== channel));
        setFocusTarget({channel, kind: 'change'});
    };
    const cancel = (channel) => {
        data.resetDraft(channel);
        close(channel);
    };

    /** Guarda el borrador de la plataforma o, con `rule`, la sugerencia. */
    const save = (channel, rule) => {
        data.saveChannel(channel, rule).then((result) => {
            if (result.busy) return;
            if (result.forbidden) {
                toast.error(t(`${NS}.forbidden`));
                setOpenChannels([]);
            } else if (!result.ok) {
                toast.error(t(`${NS}.save_error`));
            } else {
                toast.success(t(`${NS}.saved_ok`));
                close(channel);
            }
        });
    };

    const suggestionText = (suggestion) => {
        const evidence = suggestion.evidence || {};
        const levelPlural = evidence.level === 'AD'
            ? adLevelLabel(t, {count: 2})
            : adgroupLevelLabel(t, suggestion.channel, {count: 2});
        return t(suggestionTextKey(suggestion), {
            platform: platformName(suggestion.channel), param: evidence.param, levelPlural,
            pct: percentText(evidence.match_pct, language), interpolation: RAW,
        });
    };

    const platformCell = (channel) => (
        <span className="apl-ga4-attr__platform">
            <PlatformLogo channel={channel} size={20} className="-xs"/>
            <span className="apl-ga4-attr__name">{platformName(channel)}</span>
        </span>
    );

    const renderLine = (rule) => {
        const {channel} = rule;
        if (isAutomatic(channel, rule)) {
            return (
                <li key={channel} className="apl-ga4-attr__line" data-testid={`ga4-attr-line-${channel}`}>
                    <div className="apl-ga4-attr__main">
                        {platformCell(channel)}
                        <span className="apl-ga4-attr__summary">{t('ga4_attribution.summary.google_auto')}</span>
                    </div>
                </li>
            );
        }

        const platform = platformName(channel);
        const editable = rowIsEditable(rule, data.canEdit);
        const isOpen = editable && openChannels.includes(channel);
        const saving = data.savingChannel === channel;
        const busy = data.savingChannel !== null;
        const suggestion = editable && !isOpen
            ? data.suggestions.find((each) => each.channel === channel && suggestionOffered(each, data.saved[channel]))
            : null;
        const suggestionId = `apl-ga4-attr-suggestion-${channel}`;

        return (
            <li key={channel} className={`apl-ga4-attr__line${isOpen ? ' -open' : ''}`} data-testid={`ga4-attr-line-${channel}`}>
                <div className="apl-ga4-attr__main">
                    {platformCell(channel)}
                    {suggestion ? (
                        <span className="apl-ga4-attr__suggestion" id={suggestionId}
                              data-testid={`ga4-attr-suggestion-${channel}`}>
                            <Sparkle size={16} weight="bold" aria-hidden="true"/>
                            <span>{suggestionText(suggestion)}</span>
                        </span>
                    ) : (
                        <span className="apl-ga4-attr__summary" data-testid={`ga4-attr-summary-${channel}`}>
                            {ruleSummaryText(t, channel, data.saved[channel], {compact: true})}
                        </span>
                    )}
                    {editable && !isOpen && (
                        <span className="apl-ga4-attr__actions">
                            {suggestion && (
                                <button type="button" className="apl-btn -sm -primary" disabled={busy}
                                        aria-busy={saving} aria-describedby={suggestionId}
                                        aria-label={t(`${NS}.suggestion_apply_aria`, {platform, interpolation: RAW})}
                                        onClick={() => save(channel, suggestion.rule)}>
                                    {saving && <Spinner color="negative very-small"/>}
                                    {t(`${NS}.suggestion_apply`)}
                                </button>
                            )}
                            <button type="button" className="apl-link" disabled={busy}
                                    ref={(element) => { changeButtons.current[channel] = element; }}
                                    aria-label={t(`${NS}.change_aria`, {platform, interpolation: RAW})}
                                    onClick={() => open(channel)}>
                                {t(`${NS}.change`)}
                            </button>
                        </span>
                    )}
                </div>
                {isOpen && (
                    <div className="apl-ga4-attr__edit" data-testid={`ga4-attr-edit-${channel}`}
                         ref={(element) => { editors.current[channel] = element; }}>
                        <AttributionRuleFields channel={channel} rule={data.draft[channel]} layout="row"
                                               disabled={saving}
                                               onChange={(field, value) => data.setField(channel, field, value)}/>
                        <div className="apl-ga4-attr__edit-foot">
                            <button type="button" className="apl-link" disabled={saving} onClick={() => cancel(channel)}>
                                {t(`${NS}.cancel`)}
                            </button>
                            {data.isRowDirty(channel) && (
                                <button type="button" className="apl-btn -sm -primary" disabled={busy}
                                        aria-busy={saving} onClick={() => save(channel)}>
                                    {saving && <Spinner color="negative very-small"/>}
                                    {t(saving ? `${NS}.saving` : `${NS}.save`)}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </li>
        );
    };

    return frame(
        <>
            <p className="apl-ga4-attr__subtitle">{t(`${NS}.subtitle`)}</p>

            {readOnly && (
                <p className="apl-ga4-attr__readonly" data-testid="ga4-attr-readonly">
                    <Lock size={16} weight="bold" aria-hidden="true"/>
                    <span>{t(`${NS}.forbidden`)}</span>
                </p>
            )}

            <ul className="apl-ga4-attr__lines">
                {lines.map(renderLine)}
            </ul>

            {data.overrides.length > 0 && (
                <div className="apl-ga4-attr__overrides" data-testid="ga4-attr-overrides">
                    <h5 className="apl-ga4-attr__overrides-title">{t(`${NS}.overrides_title`, {count: data.overrides.length})}</h5>
                    <ul className="apl-ga4-attr__overrides-list">
                        {data.overrides.map((override) => (
                            <li key={override.client_ad_account_id} className="apl-ga4-attr__override">
                                <span className="apl-ga4-attr__override-text">
                                    <Link className="apl-link" to={`/client/${override.client_id}`}>{override.client_name}</Link>
                                    <span className="apl-meta">
                                        {platformName(override.channel)} · {override.account_name}
                                        {override.platform_account_id ? ` [${override.platform_account_id}]` : ''}
                                    </span>
                                </span>
                                <button type="button" className="apl-link" onClick={() => setAccount(override)}>
                                    {t(`${NS}.overrides_view`)}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <Ga4AttributionModal isOpen={account !== null} onClose={() => setAccount(null)}
                                 clientAdAccountId={shownAccount ? shownAccount.client_ad_account_id : null}
                                 channel={shownAccount ? shownAccount.channel : null}
                                 accountName={shownAccount ? `${shownAccount.client_name} · ${shownAccount.account_name}` : null}
                                 seeRuleLink={false}
                                 onSaved={data.refresh}/>
        </>,
    );
}

export default Ga4AttributionRulesCard;
